import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { GeneratedFormDialog } from '@adornis/cms/client/generated-form-dialog.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/legacy/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { AdornisUser, BaseUser, changePassword } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { registerAdminUser } from '../api/registerAdminUser.js';

@customElement('buddy-page-admin-users')
export class BuddyPageAdminUsers extends ChemistryLitElement {
  currentUser = new RXController(this, currentUser());
  translator = new TranslationController(this);

  // Hoisted so x-data-view doesn't see a new options reference on every render
  // (translation updates re-render the page; without a stable ref the data view
  // re-subscribes subscribePaginatedAndFiltered in a loop).
  private _dataViewOptions: DataViewOptions<typeof AdornisUser> = {
    class: AdornisUser,
    isSortable: false,
    isColumnSelectable: false,
    view: new View({
      filter: new AdornisFilter({
        aspects: [new StringAspect({ mode: 'equals', fieldName: '_class', value: AdornisUser._class })],
      }),
    }),
    customColumns: [
      { fieldName: 'username' },
      { fieldName: 'email' },
      {
        customID: 'options',
        columnWidth: 'max-content',
        getFieldMapping: e => html`
          <x-flex horizontal space="sm">
            <x-button
              mode="icon"
              icon="key"
              @click=${async () => {
                const newPW = await XDialog.prompt('neues Passwort');
                void XDialog.guard(() => changePassword(e._id, newPW));
              }}
            ></x-button>
            ${
              e._id !== this.currentUser.value?._id
                ? html`
                    <x-button
                      mode="icon"
                      icon="delete"
                      @click=${() => XDialog.guard(() => e.remove(), { hasToConfirm: true })}
                    ></x-button>
                  `
                : nothing
            }
          </x-flex>
        `,
      },
    ],
  };

  protected async _registerAdminUser() {
    const user = await GeneratedFormDialog.edit(new BaseUser({}));
    if (!user) return;
    void XDialog.guard(() => registerAdminUser(user)());
  }

  override render() {
    return html`
      <x-flex space="lg">
        <bh-breadcrumb .goUp=${() => goTo('/')}></bh-breadcrumb>
        <x-flex space="md" horizontal crossaxis-center>
          <x-h1 flex> ${this.translator.translate('route', 'admin-users' as any)} </x-h1>
          <x-button mode="outline" icon="add" @click=${async () => await this._createNewAdminUser()}>
            Neuen Admin-Nutzer erstellen
          </x-button>
          <x-button mode="outline" icon="add" @click=${async () => await this._registerAdminUser()}>
            ${this.translator.translate('buddy', 'create-user' as any)}
          </x-button>
        </x-flex>

        <x-data-view .options=${this._dataViewOptions}></x-data-view>
      </x-flex>
    `;
  }

  async _createNewAdminUser() {
    const user = await GeneratedFormDialog.edit(new BaseUser({}));
    if (!user) return;
    void XDialog.guard(() => registerAdminUser(user)());
  }
}
